// eventDetailsHelpers — EventDetails.js から純粋な定数/ヘルパを分離（挙動不変）。
// コンポーネント状態に依存しないモジュールレベルの純粋部分のみを集約する。
// （stageSeatingPlannerUtils/Constants と同じ分割パターン。）

import { EVENT_PARTICIPANT_STATUSES } from '../../../utils/eventParticipation';

export const API_URL = process.env.REACT_APP_API_URL;
export const FLYER_IMAGE_PATH = (process.env.REACT_APP_FLYER_IMAGE_PATH || '/api/images/flyers').replace(/\/$/, '');
export const BANNER_IMAGE_PATH = (
  process.env.REACT_APP_EVENT_BANNER_IMAGE_PATH ||
  process.env.REACT_APP_BANNER_IMAGE_PATH ||
  '/api/images/event_banners'
).replace(/\/$/, '');
export const ADMIN_PROFILE_IMAGE_PATH = (
  process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH || '/api/images/admin_profiles'
).replace(/\/$/, '');
export const ARTIST_PROFILE_IMAGE_PATH = (process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH || '/api/images/artist_profiles').replace(/\/$/, '');
export const PARTICIPANT_STATUSES = EVENT_PARTICIPANT_STATUSES;
export const PERFORMANCE_TYPE_JAPANESE = {
  single: '単発公演',
  first: '初回公演',
  additional: '追加公演',
};
export const TASK_STATUS_LABELS = {
  todo: '未着手',
  in_progress: '進行中',
  blocked: '要確認',
  done: '完了',
};
export const TASK_PRIORITY_LABELS = {
  low: '低',
  normal: '通常',
  high: '高',
  urgent: '緊急',
};
export const TASK_PRIORITY_ORDER = {
  urgent: 4,
  high: 3,
  normal: 2,
  low: 1,
};
export const normalizeSelectedOptions = (value) => {
  const normalizeOption = (item) => {
    if (typeof item === 'string') return item.trim();
    return String(item?.label || item?.name || item?.title || '').trim();
  };
  if (Array.isArray(value)) {
    return value.map(normalizeOption).filter(Boolean);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      return normalizeSelectedOptions(parsed);
    } catch {
      return text
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return [];
};
export const EVENT_TASK_FOCUS_LIMIT = 6;
export const TASK_DUE_DAY_MS = 24 * 60 * 60 * 1000;
export const TASK_STATUS_OPTIONS = [
  { id: 'todo', label: '未着手' },
  { id: 'done', label: '完了' },
];
export const TASK_COMMENT_REACTION_OPTIONS = [
  { emoji: '👍', label: 'いいね' },
  { emoji: '👀', label: '確認' },
  { emoji: '🙌', label: '助かる' },
  { emoji: '🎉', label: 'おめでとう' },
  { emoji: '❤️', label: 'ありがとう' },
];
export const TASK_COMMENT_REACTION_EMOJI_SET = new Set(
  TASK_COMMENT_REACTION_OPTIONS.map((item) => item.emoji)
);

export const mapEventTaskCommentReactions = (comment) => {
  const map = new Map();
  const reactions = Array.isArray(comment?.reactions) ? comment.reactions : [];
  reactions.forEach((reaction) => {
    const emoji = String(reaction?.emoji || '').trim();
    if (!TASK_COMMENT_REACTION_EMOJI_SET.has(emoji)) return;
    const count = Number.parseInt(String(reaction?.count ?? reaction?.reactionCount ?? 0), 10);
    if (!Number.isFinite(count) || count <= 0) return;
    map.set(emoji, {
      count,
      reactedByMe: Boolean(reaction?.reactedByMe ?? reaction?.reacted_by_me),
    });
  });
  return map;
};

export const normalizeTaskPriority = (value) => {
  const key = String(value || '')
    .trim()
    .toLowerCase();
  return TASK_PRIORITY_LABELS[key] ? key : 'normal';
};

export const isTaskDoneStatus = (value) => {
  const key = String(value || '')
    .trim()
    .toLowerCase();
  return key === 'done' || key === 'completed' || key === 'complete';
};

export const normalizeEventTaskStatus = (value) => (isTaskDoneStatus(value) ? 'done' : 'todo');

export const toDateKey = (value) => {
  if (!value) return '';
  const source = String(value).trim();
  if (!source) return '';
  const head = source.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : '';
};

export const parseDateKeyUtc = (value) => {
  const key = toDateKey(value);
  if (!key) return null;
  const [year, month, day] = key.split('-').map((item) => Number(item));
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  return new Date(Date.UTC(year, month - 1, day));
};

export const calcDueDays = (dueOn, todayDateKey) => {
  const dueDate = parseDateKeyUtc(dueOn);
  const todayDate = parseDateKeyUtc(todayDateKey);
  if (!dueDate || !todayDate) return null;
  return Math.round((dueDate.getTime() - todayDate.getTime()) / TASK_DUE_DAY_MS);
};

export const getDueTone = (days) => {
  if (days === null) return 'none';
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= 3) return 'soon';
  if (days <= 14) return 'upcoming';
  return 'future';
};

export const formatDueHint = (days) => {
  if (days === null) return '';
  if (days < 0) return `${Math.abs(days)}日超過`;
  if (days === 0) return '今日期限';
  if (days === 1) return '明日期限';
  return `${days}日後`;
};

export const buildApiAssetUrl = (path) => {
  const base = String(API_URL || '').replace(/\/$/, '');
  return base ? `${base}${path}` : path;
};

export const getEventTaskPrimaryAssignee = (task) => {
  const assignees = Array.isArray(task?.assignees) ? task.assignees : [];
  if (assignees[0]) return assignees[0];
  const fallbackLabel =
    task?.assigneeDisplayName ||
    task?.assignee_display_name ||
    '';
  if (!fallbackLabel) return null;
  return { displayName: fallbackLabel };
};

export const getEventTaskAssigneeLabel = (task) => {
  const primary = getEventTaskPrimaryAssignee(task);
  const label =
    primary?.displayName ||
    primary?.display_name ||
    primary?.fullName ||
    primary?.full_name ||
    primary?.username ||
    task?.assigneeDisplayName ||
    task?.assignee_display_name ||
    '';
  return String(label || '').trim() || '未割当';
};

export const getEventTaskAssigneeAvatarUrl = (task) => {
  const primary = getEventTaskPrimaryAssignee(task);
  const key = String(primary?.profilePicture || primary?.profile_picture || '').trim();
  if (!key) return '';
  const encoded = encodeURIComponent(key);
  if (!encoded) return '';
  return appendVariant(buildApiAssetUrl(`${ADMIN_PROFILE_IMAGE_PATH}/${encoded}`), 'proxy');
};

export const getEventTaskCommentAuthorName = (comment) => {
  const label =
    comment?.authorDisplayName ||
    comment?.author_display_name ||
    comment?.createdByDisplayName ||
    comment?.created_by_display_name ||
    '';
  return String(label || '').trim() || '管理者';
};

export const getEventTaskCommentAuthorAvatarUrl = (comment) => {
  const key =
    comment?.authorProfilePicture ||
    comment?.author_profile_picture ||
    comment?.createdByProfilePicture ||
    comment?.created_by_profile_picture ||
    '';
  const normalized = String(key || '').trim();
  if (!normalized) return '';
  const encoded = encodeURIComponent(normalized);
  if (!encoded) return '';
  return appendVariant(buildApiAssetUrl(`${ADMIN_PROFILE_IMAGE_PATH}/${encoded}`), 'proxy');
};

export const appendVariant = (url, variant) => {
  if (!url || !variant) return url || '';
  if (/[?&]variant=/.test(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}variant=${encodeURIComponent(variant)}`;
};

export const toTimestamp = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatYenText = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const safe = Math.max(0, Math.floor(num));
  return `${safe.toLocaleString('ja-JP')}円`;
};

export const formatFileSize = (size) => {
  const num = Number(size);
  if (!Number.isFinite(num) || num <= 0) return 'サイズ不明';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  if (num < 1024 * 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  return `${(num / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export const firstNonEmpty = (...vals) => vals.find((v) => typeof v === 'string' && v.trim()) || '';

export const buildParticipantProfile = (artist) => {
  if (!artist) return null;
  const name = firstNonEmpty(artist.name, artist.artist_name);
  const nameEn = firstNonEmpty(artist.name_en, artist.artist_name_en, artist.nameEn);
  const partLabel = firstNonEmpty(artist.partLabel, artist.part, artist.mainPartLabel, artist.mainPart);
  return {
    id: artist.artist_id || artist.id || null,
    name,
    nameEn,
    initial: (name || '').trim().charAt(0) || '',
    pic: artist.profile_picture || artist.pic || artist.artist_profile_picture || '',
    part: partLabel,
    isChief: !!artist.is_chief || !!artist.isChief,
    isConcertmaster: !!artist.is_concertmaster || !!artist.isConcertmaster,
    customRoles: Array.isArray(artist.custom_roles)
      ? artist.custom_roles
      : Array.isArray(artist.customRoles)
      ? artist.customRoles
      : [],
    instagram: artist.instagram_url || '',
    twitter: artist.twitter_url || '',
    facebook: artist.facebook_url || '',
    hp: artist.hp_url || artist.homepage_url || '',
    comment: firstNonEmpty(
      artist.profile_one_liner,
      artist.one_liner,
      artist.comment,
      artist.bio,
      artist.notes
    ),
  };
};

export const normalizeGenderBucket = (value) => {
  const key = String(value || '').toLowerCase();
  if (key === 'male' || key === 'man') return 'male';
  if (key === 'female' || key === 'woman') return 'female';
  return 'unknown';
};

export const calcAgeFromParts = (yearValue, monthValue, dayValue) => {
  if (!yearValue) return null;
  const year = Number.parseInt(yearValue, 10);
  if (!Number.isFinite(year)) return null;
  const month = monthValue ? Number.parseInt(monthValue, 10) : null;
  const day = dayValue ? Number.parseInt(dayValue, 10) : null;
  const today = new Date();
  let age = today.getFullYear() - year;
  if (month !== null) {
    const monthDiff = (today.getMonth() + 1) - month;
    if (monthDiff < 0) age -= 1;
    else if (monthDiff === 0 && day !== null && today.getDate() < day) age -= 1;
  }
  return age;
};

// PG int[] "{1,2,3}" -> number[]
export const parsePgIntArray = (s) => {
  const t = String(s || '').trim();
  if (t.startsWith('{') && t.endsWith('}')) {
    return t
      .slice(1, -1)
      .split(',')
      .map((x) => Number(x))
      .filter(Number.isInteger);
  }
  return null;
};

export const getDefaultColumnAlign = (id) => {
  if (id === 'startTime' || id === 'duration') return 'center';
  return 'left';
};

export const DEFAULT_TITLE_STYLE = {
  bgColor: 'var(--slate-100)',
  textColor: 'var(--slate-900)',
  fontSize: 14,
  bold: true,
  height: 36,
};

export const DEFAULT_TIMETABLE_COLUMNS = [
  { id: 'startTime', label: '時刻', align: getDefaultColumnAlign('startTime') },
  { id: 'duration', label: '所要時間', align: getDefaultColumnAlign('duration') },
  { id: 'item', label: '進行・曲目', align: getDefaultColumnAlign('item') },
];
export const buildAddress = (v = {}) => [v?.prefecture, v?.city, v?.address].filter(Boolean).join(' ').trim();
export const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);
export const SUPPORTED_IMAGE_EXTS = new Set(['.jpg', '.jpeg', '.png']);
export const UNSUPPORTED_IMAGE_MESSAGE = '対応している拡張子は .jpg / .jpeg / .png です。';
export const CHAT_GROUP_MODE = Object.freeze({
  SEPARATE: 'separate',
  GROUP_UNIFIED: 'group_unified',
});
export const CHAT_GROUP_EXECUTE_MODE = Object.freeze({
  MERGE: 'merge',
  SPLIT: 'split',
});
export const CHAT_GROUP_MODE_LABEL = Object.freeze({
  [CHAT_GROUP_MODE.SEPARATE]: '分離',
  [CHAT_GROUP_MODE.GROUP_UNIFIED]: 'グループ統合',
});

export const getFileExtension = (name = '') => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
};

export const isSupportedImageFile = (file) => {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  if (type && SUPPORTED_IMAGE_TYPES.has(type)) return true;
  const ext = getFileExtension(file.name || '');
  return !!ext && SUPPORTED_IMAGE_EXTS.has(ext);
};
